import {useCallback, useEffect, useRef, useState} from "react";

import AlertComponent from "../../../helpers/alert/AlertComponent";
import {
    getAlertedProductsPage,
    getAlertedProductsParameterCatalog,
} from "../../../entities/alerted-product";
import {RESUELTA_GESTION_ID} from "../../alert-management/model/alertManagementConstants";

const PARAM_MANAGEMENT_TYPE = 39;
const PARAM_ALERT_CATEGORY = 35;
const PARAM_ALERT_MANAGEMENT = 36;
const SEARCH_DEBOUNCE_MS = 400;

// Carga los catálogos, filtros y la tabla paginada de productos alertados para
// la vista de centralización. Reutiliza el mismo servicio que "Productos
// alertados" (getAlertedProductsPage), sin la lógica de documentos de jornada.
export const useCentralizationData = () => {
    const [managementTypeOptions, setManagementTypeOptions] = useState([]);
    const [alertCategoryOptions, setAlertCategoryOptions] = useState([]);
    const [alertManagementOptions, setAlertManagementOptions] = useState([]);

    const [appliedFilters, setAppliedFilters] = useState(null);
    const [tableDataSource, setTableDataSource] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const searchDebounceRef = useRef(null);

    useEffect(() => {
        getAlertedProductsParameterCatalog(PARAM_MANAGEMENT_TYPE)
            .then((options) =>
                setManagementTypeOptions(
                    options.filter((o) => o.label?.trim().toLowerCase() !== "sin tipo de gestión")
                )
            )
            .catch(() => setManagementTypeOptions([]));
        getAlertedProductsParameterCatalog(PARAM_ALERT_CATEGORY)
            .then(setAlertCategoryOptions)
            .catch(() => setAlertCategoryOptions([]));
        getAlertedProductsParameterCatalog(PARAM_ALERT_MANAGEMENT)
            .then(setAlertManagementOptions)
            .catch(() => setAlertManagementOptions([]));
    }, []);

    useEffect(() => {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchValue);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchValue]);

    useEffect(() => {
        if (!appliedFilters) return undefined;

        let cancelled = false;
        const fetchTable = async () => {
            setTableLoading(true);
            try {
                const result = await getAlertedProductsPage({
                    ...appliedFilters,
                    // Centralización siempre consulta productos con gestión de alerta
                    // RESUELTA (gestion_alerta=5261), sin importar los demás filtros.
                    alertManagement: {value: RESUELTA_GESTION_ID},
                    search: debouncedSearch,
                    page: currentPage,
                    pageSize,
                });
                if (cancelled) return;
                setTableDataSource(result.rows);
                setTotalRecords(result.meta?.total_registros ?? 0);
            } catch {
                if (cancelled) return;
                setTableDataSource([]);
                AlertComponent.error("Error", "No fue posible cargar los productos alertados");
            } finally {
                if (!cancelled) setTableLoading(false);
            }
        };

        fetchTable();
        return () => {
            cancelled = true;
        };
    }, [appliedFilters, currentPage, pageSize, debouncedSearch]);

    const applyFilters = useCallback((nextFilters) => {
        if (!nextFilters?.operationalDay?.value) return;
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
    }, []);

    const resetFilters = useCallback(() => {
        setAppliedFilters(null);
        setTableDataSource([]);
        setTotalRecords(0);
        setCurrentPage(1);
        setSearchValue("");
        setDebouncedSearch("");
    }, []);

    const changePage = useCallback((page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    }, []);

    const changeJourney = useCallback(() => {
    }, []);

    return {
        managementTypeOptions,
        alertCategoryOptions,
        alertManagementOptions,
        appliedFilters,
        tableDataSource,
        tableLoading,
        totalRecords,
        currentPage,
        pageSize,
        searchValue,
        setSearchValue,
        applyFilters,
        resetFilters,
        changePage,
        changeJourney,
    };
};